<script lang="ts">
	import { getCustomerToken } from '$lib/js/magento/cart/cart'
	import { is_set_object, isCustomerLoggedIn } from '$lib/js/helpers'
	import {
		storeCustomerInfo,
		storeLoginModal,
		storeProductToAdd,
		storeSnackbar,
		storeWishlists,
		storeActiveWishlist
	} from '$lib/js/stores'
	import { t } from 'svelte-i18n-lingui'
	import { getContextScope } from '$lib/js/helpers/i18n'
	import { onMount } from 'svelte'
	import { page } from '$app/state'
	import type { BaseProduct } from '$lib/js/elasticsearch/types'

	interface Props {
		product: BaseProduct;
		type?: string;
	}

	let { product, type = '' }: Props = $props();

	const scope = getContextScope()

	let isInWishlist = $derived(() => {
		return ($storeActiveWishlist?.skus?.includes(product.sku) || page.url.pathname.includes('customer/wishlists')) ?? false
	})

	async function toggleWishlist() {
		const token = await getCustomerToken()

		if (!isCustomerLoggedIn(token)) {
			$storeProductToAdd = product
			$storeLoginModal = true
			return
		}

		try {
			const userEmail = $storeCustomerInfo?.email

			if (!userEmail) {
				throw new Error('User email not found')
			}

			const response = await fetch(`/${scope}/endpoints/wishlist`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					email: userEmail,
					sku: product.sku,
					action: isInWishlist() ? 'remove' : 'add',
					wishlistId: $storeActiveWishlist?.id
				}),
			})

			const result = await response.json()
			
			if (!result.success) {
				throw new Error(result.error || 'Failed to update wishlist')
			}
			$storeActiveWishlist = result.data

			// Update the wishlist in the store
			if (isInWishlist()) {
				$storeWishlists = $storeWishlists.map(wishlist => {
					if (wishlist.id === $storeActiveWishlist.id) {
						return { ...wishlist, skus: wishlist.skus.filter(sku => sku !== product.sku) }
					}
					return wishlist
				})
			} else {
				$storeWishlists = $storeWishlists.map(wishlist => {
					if (wishlist.id === $storeActiveWishlist.id) {
						return { ...wishlist, skus: [...wishlist.skus, product.sku] }
					}
					return wishlist
				})
			}

			$storeSnackbar.message = isInWishlist()
				? $t`Product added to wishlist` + ` <a href="/${scope}/customer/wishlists">${$storeActiveWishlist.name}</a>`
				: $t`Product removed from wishlist` + ` <a href="/${scope}/customer/wishlists">${$storeActiveWishlist.name}</a>`
			$storeSnackbar.show = true
		} catch (error) {
			console.error('Wishlist operation failed:', error)
			$storeSnackbar.message = $t`Failed to update wishlist`
			$storeSnackbar.show = true
		}
	}

	onMount(() => {
		$storeProductToAdd = product
	})
</script>

<button
	class="wishlist-button {type}"
	onclick={() => toggleWishlist()}
	aria-label={isInWishlist() ? $t`Remove from wishlist` : $t`Add to wishlist`}
>
	<div class="heart-icon">
		<span class="icon {isInWishlist() ? 'icon-heart-full' : 'icon-heart'}"></span>
	</div>
</button>

<style lang="scss">
	.wishlist-button {
		background: none;
		border: none;
		cursor: pointer;
		border-radius: 50%;
		width: 50px;
		height: 50px;
		transition: transform 0.2s ease;
		z-index: 2;

		&.product {
			position: absolute;
			top: 10px;
			right: 0;
			background-color: white;
			box-shadow: 0 2px 10px 5px rgba(0, 0, 0, 0.1);
		}

		&:hover {
			transform: scale(1.1);
		}

		.heart-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			color: var(--main);
			font-size: 20px;
			transition: color 0.2s ease;

			span {
				font-weight: 400;
			}
		}
	}
</style>
